import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../lib/api';

export interface Agent {
  id: string;
  name: string;
  systemPrompt: string;
  role: 'creator' | 'executor' | 'researcher';
  scope: 'global' | 'project';
  userId: string | null;
  projectId: string | null;
  groupId: string | null;
  allowedTools: string[] | null;
  defaultModel: string | null;
  defaultCwd: string | null;
  maxTurnsDefault: number | null;
  maxBudgetDefault: number | null;
  autoMcpServers: string[] | null;
  isSystem: boolean;
  createdAt: string;
  updatedAt: string;
  _resolved?: {
    systemPrompt: string;
    allowedTools: string[] | null;
    defaultModel: string | null;
    defaultCwd: string | null;
    maxTurnsDefault: number | null;
    maxBudgetDefault: number | null;
  };
}

export function useAllAgents() {
  return useQuery<Agent[]>({
    queryKey: ['agents', 'all'],
    queryFn: async () => {
      const res = await api.get('/api/agents/global');
      return res.data;
    },
  });
}

export function useAgents(projectId: string | undefined) {
  return useQuery<Agent[]>({
    queryKey: ['projects', projectId, 'agents'],
    queryFn: async () => {
      const res = await api.get(`/api/projects/${projectId}/agents`);
      return res.data;
    },
    enabled: !!projectId,
  });
}

export function useAgent(id: string | undefined) {
  return useQuery<Agent>({
    queryKey: ['agents', id],
    queryFn: async () => {
      const res = await api.get(`/api/agents/${id}`);
      return res.data;
    },
    enabled: !!id,
  });
}

export function useCreateAgent(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: { name: string; role: Agent['role']; systemPrompt?: string }) => {
      const res = await api.post(`/api/projects/${projectId}/agents`, data);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects', projectId, 'agents'] });
    },
  });
}

export function useUpdateAgent() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...data }: { id: string; name?: string; role?: Agent['role']; systemPrompt?: string }) => {
      const res = await api.put(`/api/agents/${id}`, data);
      return res.data;
    },
    onSuccess: (data: Agent) => {
      queryClient.invalidateQueries({ queryKey: ['agents', data.id] });
      queryClient.invalidateQueries({ queryKey: ['projects', data.projectId, 'agents'] });
    },
  });
}

export function useDeleteAgent() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/api/agents/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects'] });
    },
  });
}

export function useDismissAgent() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await api.post(`/api/agents/${id}/dismiss`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['agents'] });
      queryClient.invalidateQueries({ queryKey: ['projects'] });
    },
  });
}

export function useRestoreAgent() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await api.post(`/api/agents/${id}/restore`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['agents'] });
      queryClient.invalidateQueries({ queryKey: ['projects'] });
    },
  });
}
